import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Perfil } from "../../../pages/easyReport/perfilRelatorio/Index";
import { ReactComponent as SearchReportPerfil } from "../../../assets/easyReport/iconSvg/searchReportPerfil.svg";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { useStyle } from "./styles";
import {
  AddOutlined,
  ArrowBackOutlined,
  DeleteOutline,
  Save,
  TuneOutlined,
} from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import { PdfPreview } from "../pdfPreview/pdfPreview";
import * as JSON5 from "json5";

interface ProcessedFuncao {
  idFuncao: string;
  nomeFuncao: string;
  view: string;
  code: string;
  styles: { [key: string]: any };
  variaveis: Variaveis[];
}

export const RightBoxContent = ({
  selectedPerfil,
  edit,
  setEdit,
  perfis,
  setPerfis,
  handleDrawerClose,
  setSelectedPerfil,
}: {
  selectedPerfil: Perfil | null;
  edit: boolean;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  perfis: Perfil[];
  setPerfis: React.Dispatch<React.SetStateAction<Perfil[]>>;
  setSelectedPerfil: React.Dispatch<React.SetStateAction<Perfil | null>>;

  handleDrawerClose: () => void;
}) => {
  const theme = useTheme();
  const styles = useStyle(theme);
  const navigate = useNavigate();

  const [editedPerfil, setEditedPerfil] = useState<Perfil | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [funcoes, setFuncoes] = useState<
    | {
        idFuncao: string;
        nomeFuncao: string;
        view: string;
        code: string;
        styles: string;
        variaveis: { variableName: string }[];
      }[]
    | null
  >(null);

  const processedViewStyles = useMemo(() => {
    if (!selectedPerfil?.funcoes) return [];
    return selectedPerfil.funcoes.map((f) => {
      const uniqueIdentifier = `${f.nomeFuncao}_${f.idFuncao}`;
      let newView = f.view;
      let newStyles: { [key: string]: any } = {};

      try {
        const stylesObj = JSON5.parse(f.styles);
        Object.keys(stylesObj).forEach((styleKey) => {
          const newKey = `${styleKey}_${uniqueIdentifier}`;
          const regex = new RegExp(`\\b${styleKey}\\b`, "g");
          newView = newView.replace(regex, newKey);
          newStyles[newKey] = stylesObj[styleKey];
        });
      } catch (e) {
        newStyles = {};
      }

      return {
        ...f,
        view: newView,
        styles: newStyles,
      };
    });
  }, [selectedPerfil]);

  const variables = useMemo(() => {
    return processedViewStyles?.map((f) =>
      f.variaveis.map((v: any) => {
        return {
          nome: v.variableName,
          itens: v.keyName.map((k: any) => k.key),
          multiple: v.multiple,
        };
      })
    );
  }, [selectedPerfil]);

  const view = useMemo(
    () => processedViewStyles?.map((f) => f.view) || [],
    [selectedPerfil]
  );
  const code = useMemo(
    () => processedViewStyles.map((f) => f.code) || [],
    [selectedPerfil]
  );
  const stylesStr = useMemo(
    () => processedViewStyles.map((f) => f.styles) || [],
    [selectedPerfil]
  );

  useEffect(() => {
    const fetchFuncoes = async () => {
      try {
        const response = await api.get("/easyReport/v2/findManyFunctions");
        setFuncoes(
          response.data.map((f: any) => ({
            idFuncao: f.id,
            nomeFuncao: f.titulo,
            code: f.code,
            view: f.view,
            styles: f.styles,
            variaveis: f.variaveis,
          }))
        );
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios",
          error?.response?.data?.error || "Erro ao carregar funções",
          "error",
          3000
        );
      }
    };
    fetchFuncoes();
  }, []);

  useEffect(() => {
    setEditedPerfil(selectedPerfil);
  }, [selectedPerfil]);

  const handleSave = async () => {
    try {
      if (!editedPerfil) return;
      await api.put(`/easyReport/v2/editProfile`, {
        id: editedPerfil.id,
        titulo: editedPerfil.titulo,
        descricao: editedPerfil.descricao,
        funcoes: editedPerfil.funcoes.map((f) => f.idFuncao),
      });
      setPerfis((prev) =>
        prev.map((perfil) => {
          if (perfil.id === editedPerfil?.id) return editedPerfil;
          return perfil;
        })
      );
      setSelectedPerfil(editedPerfil);
      setEdit(false);
      setHasChanges(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        "Perfil atualizado com sucesso",
        "success",
        3000
      );
    } catch (error: any) {
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        error?.response?.data?.error || "Erro ao salvar perfil",
        "error",
        3000
      );
    }
  };

  const handleAddFuncao = () => {
    if (!editedPerfil) return;
    setHasChanges(true);
    const selectedIds = editedPerfil.funcoes.map((f) => f.idFuncao);
    const availableOptions = funcoes?.filter(
      (f) => !selectedIds.includes(f.idFuncao)
    );
    if (!availableOptions || availableOptions.length === 0) return;
    setEditedPerfil({
      ...editedPerfil,
      funcoes: [...(editedPerfil.funcoes || []), availableOptions[0]],
    });
  };

  const handleDeleteFuncao = (index: number) => {
    if (!editedPerfil) return;
    setHasChanges(true);
    const newFuncoes = [...(editedPerfil.funcoes || [])];
    newFuncoes.splice(index, 1);
    setEditedPerfil({
      ...editedPerfil,
      funcoes: newFuncoes,
    });
  };

  const handleSelectChange = (index: number, value: string) => {
    if (!editedPerfil) return;
    const newFuncoes = [...editedPerfil.funcoes];
    const selectedFuncao = funcoes?.find((f) => f.idFuncao === value);
    if (!selectedFuncao) return;
    newFuncoes[index] = selectedFuncao;
    setEditedPerfil((prev) => (prev ? { ...prev, funcoes: newFuncoes } : prev));
  };

  const isTituloDuplicated =
    !!editedPerfil &&
    perfis.some(
      (p) =>
        p.titulo.trim().toLowerCase() ===
          editedPerfil.titulo.trim().toLowerCase() && p.id !== editedPerfil.id
    );

  return (
    <Paper sx={styles.paper}>
      {editedPerfil ? (
        <Box sx={styles.container}>
          <Box sx={styles.backIcon}>
            <IconButton onClick={handleDrawerClose} sx={{ fontSize: 32, p: 1 }}>
              <ArrowBackOutlined sx={{ fontSize: 32 }} />
            </IconButton>
          </Box>
          <Box sx={styles.headerContainer}>
            <Box sx={styles.iconContainer}>
              <Box sx={styles.icon}>
                <AccountBoxIcon sx={{ fontSize: 40 }} />
              </Box>
              <Box>
                <Typography sx={styles.title} variant="h6" noWrap>
                  {editedPerfil.titulo}
                </Typography>
                <Typography
                  sx={styles.titleDescription}
                  color={theme.palette.text.secondary}
                  variant="body2"
                >
                  {editedPerfil.descricao}
                </Typography>
              </Box>
            </Box>
            {edit && (
              <Button
                color="success"
                sx={styles.saveButton}
                onClick={handleSave}
                disabled={isTituloDuplicated || !hasChanges}
              >
                <Save />
                <Typography>Salvar</Typography>
              </Button>
            )}
          </Box>
          {!edit && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <PdfPreview
                variableNames={variables}
                rawContent={view}
                code={code}
                styles={stylesStr}
              />
            </Box>
          )}
          {edit && (
            <Box>
              <Box sx={styles.textField}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Adicione um título
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  slotProps={{
                    input: {
                      sx: {
                        color: theme.palette.text.secondary,
                      },
                    },
                  }}
                  size="small"
                  value={editedPerfil.titulo}
                  onChange={(e) => {
                    setHasChanges(true);
                    setEditedPerfil((prev) =>
                      prev ? { ...prev, titulo: e.target.value } : prev
                    );
                  }}
                  placeholder="Digite o título"
                  error={isTituloDuplicated}
                  helperText={
                    isTituloDuplicated ? "Título já existe. Escolha outro." : ""
                  }
                />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Descrição
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={editedPerfil.descricao}
                  onChange={(e) => {
                    setHasChanges(true);
                    setEditedPerfil((prev) =>
                      prev ? { ...prev, descricao: e.target.value } : prev
                    );
                  }}
                  placeholder="Digite a descrição"
                  multiline
                  minRows={4}
                  slotProps={{
                    input: {
                      sx: {
                        color: theme.palette.text.secondary,
                      },
                    },
                  }}
                />
                <Box sx={styles.selectContainer}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Selecione a função
                  </Typography>
                  <Button
                    onClick={() =>
                      navigate("/easyReportV2/perfilRelatorios/novaFuncao")
                    }
                    variant="outlined"
                  >
                    <TuneOutlined />
                    <Typography>Criar função</Typography>
                  </Button>
                </Box>
                {editedPerfil?.funcoes.map((func, index) => {
                  const selectedIds = editedPerfil.funcoes
                    .filter((_, i) => i !== index)
                    .map((f) => f.idFuncao);
                  const availableOptions = funcoes?.filter(
                    (f) => !selectedIds.includes(f.idFuncao)
                  );
                  return (
                    <Box sx={{ display: "flex" }} key={func.idFuncao}>
                      <Select
                        value={func.idFuncao}
                        fullWidth
                        key={`${func.idFuncao}-Select`}
                        onChange={(e) => {
                          setHasChanges(true);
                          handleSelectChange(index, e.target.value);
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 250,
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {availableOptions?.map((f) => (
                          <MenuItem value={f.idFuncao} key={f.idFuncao}>
                            {f.nomeFuncao}
                          </MenuItem>
                        ))}
                      </Select>
                      <IconButton
                        onClick={() => handleDeleteFuncao(index)}
                        disabled={!(editedPerfil.funcoes.length > 1)}
                        color="error"
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ mt: 1, mb: 2 }}>
                <Button onClick={handleAddFuncao}>
                  <AddOutlined /> Adicionar Função
                </Button>
              </Box>
            </Box>
          )}
          {edit && (
            <Button
              color="success"
              sx={styles.saveButtonMobile}
              onClick={() => {
                handleSave();
                handleDrawerClose();
              }}
              disabled={isTituloDuplicated || !hasChanges}
            >
              <Save />
              <Typography>Salvar</Typography>
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={styles.boxTextAndIcon}>
            <SearchReportPerfil style={styles.svgStyle} />
            <Typography color="text.disabled" fontWeight={500}>
              Selecione um perfil para ver os detalhes aqui
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};
